import type { NameSource, Reel } from './reelTypes';

/**
 * PLAT-11054 — a Section renamed by hand (DE10).
 *
 * The name is recorded with `nameSource: 'user'` by default, which is what
 * protects it from any later generation (PLAT-11059). Blank clears the stored
 * name rather than storing an empty string (DS1): the useful result of "I don't
 * want my name any more" is the opening-words fallback, not a blank row.
 *
 * PLAT-11059 passes `'generated'` for a name the AI wrote. Same write, different
 * provenance — and provenance is the whole of what DE10 protects, so it is a
 * parameter here rather than a second near-identical writer.
 *
 * Pure and non-mutating — the caller writes the returned reel into
 * `clipJson.reel`, and `clipJson` is handed around by reference.
 */
export function renameSectionInReel(
  reel: Reel | undefined,
  sectionId: string,
  name: string,
  nameSource: NameSource = 'user',
): Reel {
  const trimmed = name.trim();
  const sections = { ...reel?.sections };

  if (trimmed) {
    sections[sectionId] = { name: trimmed, nameSource };
  } else {
    delete sections[sectionId];
  }

  // A fresh `chapters` when the clip had no reel — never the shared `EMPTY_REEL`
  // array, which whatever adds chapters next (PLAT-11055) would then mutate for
  // every clip in the session.
  //
  // `...reel` first, so anything a later ticket stores on the reel rides across
  // instead of being dropped by a writer that predates it — which is exactly
  // what happened to PLAT-11072's `disabledSections`.
  return { ...reel, chapters: reel?.chapters ?? [], sections };
}
